import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Upload, 
  Play, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  FileSpreadsheet,
  Link as LinkIcon,
  Download,
  ArrowLeft,
  RefreshCw,
  Video,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axiosLib from "axios";

// Create axios instance with credentials for cookie-based auth
const axios = axiosLib.create({
  withCredentials: true
});

const TOKEN_KEY = 'prysh_session_token';
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const API = process.env.REACT_APP_BACKEND_URL;

const ShopsyBatch = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [excelFile, setExcelFile] = useState(null);
  const [googleDriveUrl, setGoogleDriveUrl] = useState("");
  const [voiceId, setVoiceId] = useState("21m00Tcm4TlvDq8ikWAM");
  const [isProcessing, setIsProcessing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [voices, setVoices] = useState([]);
  
  // Fetch available voices
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await axios.get(`${API}/api/voices`);
        if (response.data.voices) {
          setVoices(response.data.voices);
        }
      } catch (error) {
        console.error("Error fetching voices:", error);
      }
    };
    fetchVoices();
  }, []);
  
  // Poll for batch status
  useEffect(() => {
    let interval;
    if (batchId && isProcessing) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/api/shopsy/batch/${batchId}/status`);
          setBatchStatus(response.data);
          
          if (response.data.status === "completed" || response.data.status === "failed") {
            setIsProcessing(false);
            clearInterval(interval);
            
            if (response.data.successful > 0) {
              toast.success(`Batch completed! ${response.data.successful} videos generated.`);
            } else {
              toast.error("Batch completed. No videos were generated due to processing errors.");
            }
          }
        } catch (error) {
          console.error("Error fetching batch status:", error);
        }
      }, 3000);
    }
    
    return () => clearInterval(interval);
  }, [batchId, isProcessing]);
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setExcelFile(file);
        toast.success(`File selected: ${file.name}`);
      } else {
        toast.error("Please select an Excel file (.xlsx or .xls)");
      }
    }
  };
  
  const handleStartBatch = async () => {
    if (!excelFile) {
      toast.error("Please upload an Excel file with product URLs");
      return;
    }
    
    setIsProcessing(true);
    setBatchStatus(null);
    
    try {
      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("google_drive_url", googleDriveUrl);
      formData.append("voice_id", voiceId);
      
      const response = await axios.post(`${API}/api/shopsy/batch/start`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setBatchId(response.data.batch_id);
        toast.success(`Processing ${response.data.total_urls} URLs...`);
      }
    } catch (error) {
      console.error("Error starting batch:", error);
      toast.error(error.response?.data?.detail || "Failed to start batch processing");
      setIsProcessing(false);
    }
  };
  
  const getProgressPercentage = () => {
    if (!batchStatus) return 0;
    return Math.round((batchStatus.processed / batchStatus.total_urls) * 100);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/projects")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display text-xl font-bold text-foreground">Shopsy Batch</h1>
              <p className="text-sm text-muted-foreground">Automated video generation</p>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {!batchId ? (
            /* Input Form */
            <motion.div
              key="input-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Title */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Video className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-3xl font-bold mb-2">Shopsy Batch Processing</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Upload an Excel file with up to 5 product URLs to automatically generate videos
                </p>
              </div>
              
              {/* Excel Upload */}
              <div className="bg-card rounded-2xl border border-border p-8">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Excel File
                </h3>
                
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    excelFile ? "border-green-500 bg-green-50" : "border-border hover:border-primary"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="excel-upload-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {excelFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <span className="font-medium text-green-700">{excelFile.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                      <p className="font-medium mb-1">Drop Excel file here</p>
                      <p className="text-sm text-muted-foreground">or click to browse (.xlsx, .xls)</p>
                    </>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground mt-3">
                  Excel should have product URLs in the first column (max 5 URLs)
                </p>
              </div>
              
              {/* Voice Selection */}
              <div className="bg-card rounded-2xl border border-border p-8">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Video className="w-5 h-5 text-primary" />
                  Voiceover Settings
                </h3>
                
                <div className="space-y-4">
                  <label className="block">
                    <span className="text-sm font-medium text-foreground mb-2 block">Voice</span>
                    <select
                      value={voiceId}
                      onChange={(e) => setVoiceId(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="voice-select"
                    >
                      {voices.length > 0 ? (
                        voices.map((voice) => (
                          <option key={voice.voice_id} value={voice.voice_id}>
                            {voice.name}
                          </option>
                        ))
                      ) : (
                        <option value="21m00Tcm4TlvDq8ikWAM">Rachel (Default)</option>
                      )}
                    </select>
                  </label>
                </div>
              </div>
              
              {/* Google Drive URL (Optional) */}
              <div className="bg-card rounded-2xl border border-border p-8">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-primary" />
                  Google Drive Folder (Optional)
                </h3>
                
                <input
                  type="text"
                  value={googleDriveUrl}
                  onChange={(e) => setGoogleDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="google-drive-input"
                />
                
                <p className="text-xs text-muted-foreground mt-3">
                  Paste a Google Drive folder link with public edit access (coming soon)
                </p>
              </div>
              
              {/* Start Button */}
              <Button
                onClick={handleStartBatch}
                disabled={!excelFile || isProcessing}
                className="w-full py-6 text-lg font-semibold"
                data-testid="start-batch-btn"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Start Batch Processing
                  </>
                )}
              </Button>
            </motion.div>
          ) : (
            /* Progress View */
            <motion.div
              key="progress-view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Status Header */}
              <div className="text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
                  batchStatus?.status === "completed" 
                    ? "bg-green-500/10" 
                    : batchStatus?.status === "failed"
                    ? "bg-red-500/10"
                    : "bg-primary/10"
                }`}>
                  {batchStatus?.status === "completed" ? (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  ) : batchStatus?.status === "failed" ? (
                    <XCircle className="w-8 h-8 text-red-500" />
                  ) : (
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  )}
                </div>
                <h2 className="font-display text-2xl font-bold mb-2">
                  {batchStatus?.status === "completed" 
                    ? "Batch Complete!" 
                    : batchStatus?.status === "failed"
                    ? "Batch Failed"
                    : "Processing..."}
                </h2>
                {batchStatus?.current_step && batchStatus.status === "processing" && (
                  <p className="text-muted-foreground">{batchStatus.current_step}</p>
                )}
              </div>
              
              {/* Progress Bar */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">{batchStatus?.processed || 0} / {batchStatus?.total_urls || 0}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary to-primary/70"
                    initial={{ width: 0 }}
                    animate={{ width: `${getProgressPercentage()}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <div className="flex justify-between mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>{batchStatus?.successful || 0} successful</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span>{batchStatus?.failed || 0} failed</span>
                  </div>
                </div>
              </div>
              
              {/* Results */}
              {batchStatus?.results && batchStatus.results.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <h3 className="font-semibold text-lg mb-4">Results</h3>
                  <div className="space-y-3">
                    {batchStatus.results.map((result, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          result.status === "success" 
                            ? "border-green-200 bg-green-50" 
                            : "border-red-200 bg-red-50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {result.status === "success" ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                          <div>
                            <p className="font-medium text-sm">URL {result.url_index + 1}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-md">
                              {result.url}
                            </p>
                          </div>
                        </div>
                        
                        {result.status === "success" && result.video_path && (
                          <a
                            href={`${API}/api/files/${result.video_path.split('/').pop()}`}
                            download
                            className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
                            data-testid={`download-btn-${idx}`}
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        )}
                        
                        {result.status === "failed" && result.error && (
                          <span className="text-xs text-red-600 max-w-xs truncate">
                            {result.error}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Error Log */}
              {batchStatus?.error_log && batchStatus.error_log.length > 0 && (
                <div className="bg-red-50 rounded-2xl border border-red-200 p-6">
                  <h3 className="font-semibold text-lg mb-4 text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Errors
                  </h3>
                  <ul className="space-y-2 text-sm text-red-600">
                    {batchStatus.error_log.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* New Batch Button */}
              {batchStatus?.status === "completed" && (
                <Button
                  onClick={() => {
                    setBatchId(null);
                    setBatchStatus(null);
                    setExcelFile(null);
                  }}
                  variant="outline"
                  className="w-full py-6"
                  data-testid="new-batch-btn"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Start New Batch
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ShopsyBatch;
